import { API_URLS } from "@/config/urls";
import { api } from "@/lib/axios";
import { handlePostApiError } from "@/lib/humhub-api/posts/errors";
import {
  buildChannelQueryParams,
  fetchAllPinnedPostPages,
} from "@/lib/humhub-api/posts/shared";
import type {
  FavoritePostResponse,
  PaginatedResponse,
  PostResponse,
  StreamContent,
} from "@/lib/humhub-api/types";
import type { StreamSearchParams } from "@/lib/utils/streamSearchUtils";

export const getPosts = async (
  groupId: number,
  page = 1,
  perPage = 20,
  searchParams?: StreamSearchParams,
  channelId?: number | string,
  excludeChannelIds?: number[],
  channelKey?: string,
): Promise<PaginatedResponse<StreamContent>> => {
  const normalizedExcludeIds = (excludeChannelIds ?? []).filter(
    (id) => Number.isFinite(id) && id > 0,
  );
  const channelParams = buildChannelQueryParams({ channelId, channelKey });
  const params = {
    page,
    limit: perPage,
    ...(searchParams?.keyword ? { keyword: searchParams.keyword } : {}),
    ...(searchParams?.from ? { from: searchParams.from } : {}),
    ...(searchParams?.to ? { to: searchParams.to } : {}),
    ...(searchParams?.authorId ? { authorId: searchParams.authorId } : {}),
    ...channelParams,
    ...(normalizedExcludeIds.length > 0
      ? { exclude_channel_ids: normalizedExcludeIds.join(",") }
      : {}),
  };

  try {
    const response = await api.get<PaginatedResponse<StreamContent>>(
      `${API_URLS.STUDY_GROUP}/${groupId}/stream`,
      { params },
    );

    const filteredResults = response.data.results.filter(
      (post) =>
        post.metadata?.object_model === "humhub\\modules\\post\\models\\Post",
    );

    return {
      ...response.data,
      results: filteredResults,
    };
  } catch (error: unknown) {
    throw handlePostApiError(error, { label: "postsApi.getPosts error" });
  }
};

export const getPinnedPosts = async (
  containerId: number,
  channelId?: number | string,
  channelKey?: string,
): Promise<PostResponse[] | PaginatedResponse<PostResponse>> => {
  const params = buildChannelQueryParams({ channelId, channelKey });

  try {
    const response = await api.get<
      PostResponse[] | PaginatedResponse<PostResponse>
    >(`${API_URLS.POST}/container/${containerId}/pinned`, { params });

    if (Array.isArray(response.data)) {
      return response.data;
    }
    return fetchAllPinnedPostPages(containerId, params, response.data);
  } catch (error: unknown) {
    throw handlePostApiError(error, { label: "Post API Error" });
  }
};

export const getFavoritePosts = async () => {
  try {
    const response = await api.get<
      | FavoritePostResponse[]
      | { results: FavoritePostResponse[] }
      | { data: FavoritePostResponse[] }
    >(`${API_URLS.READ_LATER_POSTS}`);

    if (Array.isArray(response.data)) {
      return response.data;
    }

    if (
      response.data &&
      "results" in response.data &&
      Array.isArray(response.data.results)
    ) {
      return response.data.results;
    }

    if (
      response.data &&
      "data" in response.data &&
      Array.isArray(response.data.data)
    ) {
      return response.data.data;
    }

    return [];
  } catch (error: unknown) {
    throw handlePostApiError(error, { label: "Favorite posts API Error" });
  }
};

export const getPostByContentId = async (postContentId: number | string) => {
  try {
    const response = await api.get<PostResponse>(
      `${API_URLS.POST}/content/${postContentId}`,
    );
    return response.data;
  } catch (error: unknown) {
    throw handlePostApiError(error, { label: "Post content API Error" });
  }
};

export const getPostById = async (postId: number | string) => {
  try {
    const response = await api.get<PostResponse>(`${API_URLS.POST}/${postId}`);
    return response.data;
  } catch (error: unknown) {
    throw handlePostApiError(error, { label: "Post API Error" });
  }
};

export const getPostsByUserId = async (
  userId: number | string,
  page: number,
  perPage: number,
) => {
  try {
    const response = await api.get<PaginatedResponse<PostResponse>>(
      `${API_URLS.POST}/user/${userId}`,
      {
        params: {
          page,
          limit: perPage,
        },
      },
    );
    return response.data;
  } catch (error: unknown) {
    throw handlePostApiError(error, { label: "Post API Error" });
  }
};
